import { use, useMemo } from 'react'

import { ChatContext } from '@app/context'
import { getBaseInfo, shouldFallbackToAccount } from '@app/helpers'
import { ConversationItem, Listing, Participant } from '@app/models'
import { isB2BView } from '@app/utils/url/isB2BView'

import { useUser } from '../user/useUser'

export const useConversationDetails = (
  conversation: ConversationItem | null
) => {
  const { state } = use(ChatContext)
  const { currentUser } = useUser()

  const getAuthor = (participants: Participant[]) => {
    const currentUserId = currentUser?.id

    const createdByParticipant =
      participants.find((part) => part.id === conversation?.createdByParticipantId) ||
      null

    const mainBuyerOrRenter =
      participants.find((part) => part.role === 'MainBuyerOrRenter') || null

    if (isB2BView()) {
      return createdByParticipant || mainBuyerOrRenter || null
    }

    const findOtherParticipantByRole = (role: Participant['role']) =>
      participants.find((part) => part.role === role && part.userId !== currentUserId) || null

    const fallbackParticipant =
      participants.find((part) => part.userId !== currentUserId) || null

    const createdByOtherParticipant =
      createdByParticipant?.userId !== currentUserId ? createdByParticipant : null

    return (
      findOtherParticipantByRole('MainAgent') ||
      findOtherParticipantByRole('Agent') ||
      createdByOtherParticipant ||
      fallbackParticipant ||
      null
    )
  }

  const author = getAuthor(conversation?.participants || []) ?? null

  const listing = useMemo<Listing | null>(() => {
    if (!conversation?.context?.listingId) {
      return null
    }

    return state.listings.get(conversation.context.listingId) ?? null
  }, [state.listings, conversation?.context?.listingId])

  const { avatar, fullName, phone } = getBaseInfo(state, author, conversation)
  const shouldUseLogoTemplate = shouldFallbackToAccount(author, conversation)

  const imageUrl = useMemo(() => {
    return listing?.resources?.pictures?.items[0]?.url
  }, [listing])

  return {
    avatar,
    fullName,
    phone,
    imageUrl,
    shouldUseLogoTemplate,
    listing,
    participant: author,
  }
}
